import React, { useState, useEffect, useRef } from 'react';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence, useScroll, useMotionValueEvent } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import NavChatButton from './AIAssistant/NavChatButton';

const navItems = [
  { name: 'Home', href: '/' },
  { name: 'About', href: '/about' },
  { name: 'Programs', href: '#programs' },
  { name: 'Gallery', href: '#gallery' },
  { name: 'Contact', href: '#contact' },
];

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navRef = useRef<HTMLElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const location = useLocation();

  const { scrollY } = useScroll();
  useMotionValueEvent(scrollY, 'change', (v) => setScrolled(v > 40));

  const closeMenu = () => {
    setIsOpen(false);

  };

  const toggleMenu = () => {
    const next = !isOpen;
    setIsOpen(next);

  };

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) closeMenu();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { event.preventDefault(); setIsOpen(false); }
      if (event.key !== 'Tab') return;
      const links = Array.from(navRef.current?.querySelectorAll<HTMLElement>('button:not([disabled]), a[href]') || []).filter(el => el.getClientRects().length > 0);
      const first = links[0], last = links[links.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    };
    const onResize = () => { if (window.innerWidth >= 1024) setIsOpen(false); };
    document.addEventListener('keydown', onKey); window.addEventListener('resize', onResize);
    return () => { document.body.style.overflow = previousOverflow; document.removeEventListener('keydown', onKey); window.removeEventListener('resize', onResize); menuButtonRef.current?.focus(); };
  }, [isOpen]);

  // Close menu on route change
  useEffect(() => { closeMenu(); }, [location.pathname]);

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    if (href.startsWith('#')) {
      e.preventDefault();
      closeMenu();
      const el = document.getElementById(href.slice(1));
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      else if (location.pathname !== '/') window.location.href = `/${href}`;
    } else {
      closeMenu();
    }
  };

  return (
    <nav ref={navRef} className="fixed top-0 left-0 right-0 z-50 px-3 sm:px-4 pt-3 sm:pt-4">
      {/* ── Navbar pill ── */}
      <motion.div
        animate={{
          backdropFilter: scrolled ? 'blur(20px)' : 'blur(8px)',
          boxShadow: scrolled
            ? '0 8px 40px rgba(0,0,0,0.20)'
            : '0 2px 12px rgba(0,0,0,0.08)',
        }}
        transition={{ duration: 0.3 }}
        className={`relative z-50 flex items-center justify-between h-16 px-4 sm:px-5 rounded-2xl border transition-colors duration-300 max-w-7xl mx-auto ${
          scrolled
            ? 'bg-white/90 border-zinc-200/60'
            : 'bg-white/95 border-zinc-200/60'
        }`}
      >
        {/* ── Logo ── */}
        <Link to="/" onClick={closeMenu} className="flex items-center gap-2.5 group flex-shrink-0 min-w-0">
          <motion.div whileHover={{ rotate: [0, -8, 8, -6, 0] }} transition={{ duration: 0.5 }}>
            <img
              src="/lovable-uploads/859cd9be-32fe-431f-a361-89e4061203fb.png"
              alt="Renaissance Garden Schools"
              className="h-8 w-auto flex-shrink-0"
            />
          </motion.div>
          {/* Full name on desktop, abbreviated on tablet, hidden on mobile */}
          <span
            className={`font-display font-black tracking-tight transition-colors duration-300 hidden xl:inline text-[17px] ${
              'text-zinc-900'
            }`}
          >
            Renaissance Garden <span className="text-primary">Schools</span>
          </span>
          <span
            className={`font-display font-black tracking-tight transition-colors duration-300 hidden sm:inline xl:hidden text-[15px] ${
              'text-zinc-900'
            }`}
          >
            RG <span className="text-primary">Schools</span>
          </span>
        </Link>

        {/* ── Desktop links ── */}
        <div className="hidden lg:flex items-center gap-0.5">
          {navItems.map((item) => (
            <Link
              key={item.name}
              to={item.href}
              onClick={(e) => handleNavClick(e, item.href)}
              className={`relative px-3.5 py-2 text-[13px] font-bold rounded-xl transition-colors duration-200 ${
                'text-zinc-700 hover:text-zinc-900 hover:bg-zinc-100'
              }`}
            >
              {location.pathname === item.href && (
                <motion.div
                  layoutId="nav-indicator"
                  className={`absolute inset-0 rounded-xl ${
                    'bg-orange-50'
                  }`}
                  transition={{ type: 'spring', bounce: 0.25, duration: 0.45 }}
                />
              )}
              <span className="relative z-10">{item.name}</span>
            </Link>
          ))}

          <div className={`w-px h-5 mx-2 ${'bg-zinc-200'}`} />

          <a
            href="https://portal.renaissancegardenschools.com?school=rgs"
            target="_blank"
            rel="noopener noreferrer"
            className={`px-3.5 py-2 text-[13px] font-bold rounded-xl transition-colors ${
              scrolled ? 'text-primary hover:bg-primary/8' : 'text-primary hover:bg-white/10'
            }`}
          >
            Portal
          </a>

          <NavChatButton />

          <motion.button
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.96 }}
            onClick={() =>
              document.getElementById('contact') ? document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' }) : (window.location.href = '/#contact')
            }
            className="ml-1.5 px-5 py-2 bg-orange-700 text-white text-[13px] font-black rounded-xl shadow-[0_4px_16px_rgba(249,115,22,0.35)] hover:shadow-[0_6px_24px_rgba(249,115,22,0.45)] transition-shadow whitespace-nowrap"
          >
            Enroll Now
          </motion.button>
        </div>

        {/* ── Mobile right side: hamburger only ── */}
        <div className="lg:hidden flex items-center gap-2">
          <motion.button
            whileTap={{ scale: 0.92 }}
            ref={menuButtonRef}
            onClick={toggleMenu}
            aria-expanded={isOpen}
            aria-controls="mobile-navigation"
            className={`w-10 h-10 flex items-center justify-center rounded-xl transition-colors ${
              'bg-zinc-100 text-zinc-800 hover:bg-zinc-200'
            }`}
            aria-label={isOpen ? 'Close menu' : 'Open menu'}
          >
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={isOpen ? 'close' : 'open'}
                initial={{ rotate: -90, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                exit={{ rotate: 90, opacity: 0 }}
                transition={{ duration: 0.18 }}
              >
                {isOpen ? <X size={20} /> : <Menu size={20} />}
              </motion.div>
            </AnimatePresence>
          </motion.button>
        </div>
      </motion.div>

      {/* ── Mobile fullscreen overlay menu ── */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40 lg:hidden"
              onClick={closeMenu}
            />

            {/* Slide-down panel */}
            <motion.div
              initial={{ opacity: 0, y: -12, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -12, scale: 0.97 }}
              transition={{ type: 'spring', duration: 0.4, bounce: 0.15 }}
              id="mobile-navigation" aria-label="Mobile navigation"
              className="absolute max-h-[calc(100dvh-110px)] overflow-y-auto top-[calc(100%+8px)] left-3 right-3 lg:hidden rounded-3xl bg-white shadow-2xl border border-zinc-100 z-50"
            >
              {/* School name header */}
              <div className="px-5 pt-5 pb-4 border-b border-zinc-100">
                <p className="font-display font-black text-[15px] text-zinc-900 tracking-tight">
                  Renaissance Garden <span className="text-primary">Schools</span>
                </p>
                <p className="text-zinc-400 text-[11px] font-medium mt-0.5">
                  Our God, Our Vision, Our Strength
                </p>
              </div>

              {/* Nav links */}
              <div className="p-3 space-y-1">
                {navItems.map((item, i) => (
                  <motion.div
                    key={item.name}
                    initial={{ opacity: 0, x: -12 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.05 + 0.1 }}
                  >
                    <Link
                      to={item.href}
                      onClick={(e) => handleNavClick(e, item.href)}
                      className={`flex items-center px-4 py-3.5 rounded-2xl text-[15px] font-bold transition-colors ${
                        location.pathname === item.href
                          ? 'bg-orange-700 text-white'
                          : 'text-zinc-700 hover:bg-zinc-50 active:bg-zinc-100'
                      }`}
                    >
                      {item.name}
                    </Link>
                  </motion.div>
                ))}
              </div>

              {/* Divider + secondary links */}
              <div className="px-3 pb-3 space-y-2">
                <div className="h-px bg-zinc-100 mx-1 mb-3" />
                <a
                  href="https://portal.renaissancegardenschools.com?school=rgs"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center px-4 py-3.5 rounded-2xl text-[15px] font-bold text-primary hover:bg-primary/5 active:bg-primary/10 transition-colors"
                >
                  Student Portal ↗
                </a>
                <button
                  onClick={() => {
                    closeMenu();
                    setTimeout(() =>
                      document.getElementById('contact') ? document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' }) : (window.location.href = '/#contact'), 200
                    );
                  }}
                  className="w-full py-4 bg-orange-700 text-white text-[15px] font-black rounded-2xl shadow-[0_4px_20px_rgba(249,115,22,0.4)] active:scale-[0.98] transition-transform"
                >
                  Enroll Now
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navigation;
